import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type {
  CreateHabitInput,
  UpdateHabitInput,
  HabitWithStreak,
  CoachingCategory,
  HabitFrequency,
  DifficultyLevel,
} from '@/types';
import { CATEGORY_ICONS, HABIT_ICON_OPTIONS, IconByName } from '@/lib/category-icons';

interface HabitFormProps {
  habit?: HabitWithStreak;
  onSubmit: (input: CreateHabitInput | UpdateHabitInput) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

const categoryOptions: { value: CoachingCategory; label: string }[] = [
  { value: 'faith', label: 'Faith' },
  { value: 'family', label: 'Family' },
  { value: 'finance', label: 'Finance' },
  { value: 'fitness', label: 'Fitness' },
];

const frequencyOptions: { value: HabitFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const difficultyOptions: { value: DifficultyLevel; label: string; multiplier: string }[] = [
  { value: 'easy', label: 'Easy', multiplier: '0.5x XP' },
  { value: 'normal', label: 'Normal', multiplier: '1x XP' },
  { value: 'hard', label: 'Hard', multiplier: '2x XP' },
  { value: 'extreme', label: 'Extreme', multiplier: '5x XP' },
];

const iconOptions = HABIT_ICON_OPTIONS;

export function HabitForm({ habit, onSubmit, onCancel, isSubmitting }: HabitFormProps) {
  const isEditing = !!habit;

  const [name, setName] = useState(habit?.name ?? '');
  const [description, setDescription] = useState(habit?.description ?? '');
  const [category, setCategory] = useState<CoachingCategory>(habit?.category ?? 'fitness');
  const [frequency, setFrequency] = useState<HabitFrequency>(habit?.frequency ?? 'daily');
  const [difficulty, setDifficulty] = useState<DifficultyLevel>(habit?.difficulty ?? 'normal');
  const [icon, setIcon] = useState(habit?.icon ?? 'pin');
  const [showIconPicker, setShowIconPicker] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (isEditing) {
      await onSubmit({
        name,
        description: description || null,
        frequency,
        difficulty,
        icon,
      } as UpdateHabitInput);
    } else {
      await onSubmit({
        name,
        description: description || undefined,
        category,
        frequency,
        difficulty,
        icon,
      } as CreateHabitInput);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-charcoal-900 rounded-2xl border border-charcoal-700 shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-charcoal-700">
          <h2 className="text-xl font-bold text-white">
            {isEditing ? 'Edit Habit' : 'Create New Habit'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Icon + Name */}
          <div className="flex gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowIconPicker(!showIconPicker)}
                className="w-14 h-14 rounded-xl bg-charcoal-800 border border-charcoal-700
                         hover:border-charcoal-600 flex items-center justify-center text-2xl
                         transition-colors"
              >
                <IconByName name={icon} className="w-6 h-6 text-slate-300" />
              </button>
              <AnimatePresence>
                {showIconPicker && (
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    className="absolute left-0 top-16 z-10 p-2 bg-charcoal-800 rounded-xl border border-charcoal-700 shadow-xl"
                  >
                    <div className="grid grid-cols-4 gap-1">
                      {iconOptions.map((i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => { setIcon(i); setShowIconPicker(false); }}
                          className={`w-10 h-10 rounded-lg flex items-center justify-center hover:bg-charcoal-700 transition-colors
                                    ${icon === i ? 'bg-amber-500/20' : ''}`}
                        >
                          <IconByName name={i} className="w-5 h-5 text-slate-300" />
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Habit name"
                required
                maxLength={100}
                className="w-full px-4 py-3 rounded-xl bg-charcoal-800 border border-charcoal-700
                         text-white placeholder-slate-500
                         focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm text-slate-400 mb-1.5">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Why is this habit important to you?"
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-charcoal-800 border border-charcoal-700
                       text-white placeholder-slate-500 resize-none
                       focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            />
          </div>

          {/* Category (only for new habits) */}
          {!isEditing && (
            <div>
              <label className="block text-sm text-slate-400 mb-2">Category</label>
              <div className="grid grid-cols-4 gap-2">
                {categoryOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setCategory(opt.value)}
                    className={`p-3 rounded-xl border text-center transition-all ${
                      category === opt.value
                        ? 'border-amber-500 bg-amber-500/10 text-white'
                        : 'border-charcoal-700 bg-charcoal-800 text-slate-400 hover:border-charcoal-600'
                    }`}
                  >
                    {(() => { const Icon = CATEGORY_ICONS[opt.value]; return Icon ? <Icon className="w-5 h-5 mx-auto mb-1" /> : null; })()}
                    <span className="text-xs">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Frequency */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Frequency</label>
            <div className="grid grid-cols-4 gap-2">
              {frequencyOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setFrequency(opt.value)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-all ${
                    frequency === opt.value
                      ? 'border-amber-500 bg-amber-500/10 text-white'
                      : 'border-charcoal-700 bg-charcoal-800 text-slate-400 hover:border-charcoal-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <label className="block text-sm text-slate-400 mb-2">Difficulty</label>
            <div className="grid grid-cols-4 gap-2">
              {difficultyOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDifficulty(opt.value)}
                  className={`px-3 py-2 rounded-lg border text-center transition-all ${
                    difficulty === opt.value
                      ? 'border-amber-500 bg-amber-500/10 text-white'
                      : 'border-charcoal-700 bg-charcoal-800 text-slate-400 hover:border-charcoal-600'
                  }`}
                >
                  <span className="text-sm block">{opt.label}</span>
                  <span className="text-xs opacity-60">{opt.multiplier}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-3 rounded-xl bg-charcoal-800 text-slate-300
                       border border-charcoal-700 hover:bg-charcoal-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || isSubmitting}
              className="flex-1 px-4 py-3 rounded-xl bg-royal-gradient text-white font-medium
                       shadow-glow hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Habit'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}
